import React, { FunctionComponent, useRef, useState } from 'react'
import useFormal from '@kevinwolf/formal-web'
import * as yup from 'yup'
import { SelectField } from 'components/elements/SelectField'
import { useTranslation } from 'react-i18next'
import { TextField } from 'components/elements/TextField'
import { DateTimeField } from 'components/elements/DateTimeField'
import { usePermits } from 'components/views/subscriptions/permits/PermitsProvider'
import { formatDateTimeFieldToISO, parseDateTimeField, yupDateTimeField } from 'utils'
import { PlateNumberField } from 'components/elements/PlateNumberField'
import AddPermitDialog from 'components/views/subscriptions/permits/partials/AddPermitDialog'
import Button from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import AddIcon from '@material-ui/icons/Add'
import SearchIcon from '@material-ui/icons/Search'
import Grid from '@material-ui/core/Grid'
import ButtonGroup from '@material-ui/core/ButtonGroup'
import Popper from '@material-ui/core/Popper'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import Grow from '@material-ui/core/Grow'
import Paper from '@material-ui/core/Paper'
import ClickAwayListener from '@material-ui/core/ClickAwayListener'
import MenuList from '@material-ui/core/MenuList'
import MenuItem from '@material-ui/core/MenuItem'
import download from 'downloadjs'
import { useApi } from 'components/providers/ApiProvider'

type Props = {}
type Functions = {}
type AllProps = Props & Functions

const Toolbar: FunctionComponent<AllProps> = ({}) => {
  const { t, i18n } = useTranslation()
  const { filter, onFilter, listLoading, listZones, addZones } = usePermits()
  const api = useApi()

  const [openSearchButtonDropdown, setOpenSearchButtonDropdown] = useState(false)
  const searchButtonRef = useRef(null)
  let exportClicked = false
  const [exportLoading, setExportLoading] = useState(false)

  const [openAddPermitDialog, setOpenAddPermitDialog] = useState(false)

  const handleSearchButtonDropdownToggle = () => {
    setOpenSearchButtonDropdown((prevOpen) => !prevOpen)
  }

  const handleSearchButtonDropdownClose = (event) => {
    if (searchButtonRef.current && searchButtonRef.current.contains(event.target)) {
      return
    }

    setOpenSearchButtonDropdown(false)
  }

  const handleExportClick = () => {
    exportClicked = true
    formal.submit()
  }

  const schema = yup.object().shape({
    zone: yup.number(),
    plateNumber: yup.string(),
    client: yup.string().trim(),
    assignedFrom: yupDateTimeField(),
    assignedTill: yupDateTimeField(),
    notes: yup.string().trim(),
  })

  const formal = useFormal(filter, {
    schema,
    onSubmit: async values => {
      values = schema.cast(values)
      if (exportClicked) {
        setExportLoading(true)

        await api.exportPermitsList({
          zone_id: values.zone || undefined,
          plate_number: values.plateNumber.toUpperCase() || undefined,
          client: values.client || undefined,
          ...(values.assignedFrom && { assigned_from: formatDateTimeFieldToISO(values.assignedFrom) }),
          ...(values.assignedTill && { assigned_till: formatDateTimeFieldToISO(values.assignedTill) }),
          notes: values.notes || undefined,
        })
          .then(blob => {
            setExportLoading(false)

            download(blob, 'permits.xlsx')
          })
          .catch(() => {
            setExportLoading(false)

            alert(`${t('alerts.couldNotExportData')} ${t('alerts.tryLater')}`)
          })
      } else {
        onFilter(values)
      }
    },
  })

  return (
    <>
      {Boolean(addZones.length) && (
        <Box marginTop={1} marginBottom={2}>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddPermitDialog(true)}
          >
            {t('newPermit')}
          </Button>
        </Box>
      )}
      <form {...formal.getFormProps()}>
        <Grid container spacing={1} style={{ padding: '16px 0' }}>
          <SelectField
            GridProps={{ xs: 12, md: 4 }}
            label={t('zone')}
            optionsCustom={[
              {
                label: t('allZones'),
                id: 0,
              },
              ...listZones.sort((a, b) => a.code.localeCompare(b.code)).map(zone => {
                const localization = zone.localizations.find(l => l.language == i18n.language)

                return {
                  label: `${zone.code}${localization && localization.name ? ` ${localization.name}` : ''}`,
                  id: zone.id,
                }
              }),
            ]}
            size="small"
            typeahead
            {...formal.getFieldProps('zone')}
          />
          <PlateNumberField
            GridProps={{ xs: 6, md: 4 }}
            clearable={true}
            size="small"
            {...formal.getFieldProps('plateNumber')}
          />
          <TextField
            GridProps={{ xs: 6, md: 4 }}
            label={t('client')}
            size="small"
            {...formal.getFieldProps('client')}
          />
          <DateTimeField
            GridProps={{ xs: 12, md: true }}
            label={`${t('assignedAt')} - ${t('from')}`}
            size="small"
            {...formal.getFieldProps('assignedFrom')}
            initialFocusedDate={(new Date).setHours(0, 0, 0, 0)}
            {
              ...(formal.values.assignedTill && { maxDate: parseDateTimeField(formal.values.assignedTill) })
            }
            clearable
          />
          <DateTimeField
            GridProps={{ xs: 12, md: true }}
            label={`${t('assignedAt')} - ${t('till')}`}
            size="small"
            {...formal.getFieldProps('assignedTill')}
            initialFocusedDate={(new Date).setHours(23, 59, 59, 999)}
            {
              ...(formal.values.assignedFrom && { minDate: parseDateTimeField(formal.values.assignedFrom) })
            }
            clearable
          />
          <TextField
            GridProps={{ xs: 12, md: true }}
            label={t('notes')}
            size="small"
            {...formal.getFieldProps('notes')}
          />
          <Grid item xs={12} md={'auto'}>
            <ButtonGroup variant="contained" color="primary" fullWidth ref={searchButtonRef} style={{ height: '100%', maxHeight: 40 }}>
              <Button
                type="submit"
                startIcon={<SearchIcon />}
                disabled={listLoading}
              >
                {t('search')}
              </Button>
              <Button
                size="small"
                style={{ flex: 1 }}
                onClick={handleSearchButtonDropdownToggle}
              >
                <ArrowDropDownIcon />
              </Button>
            </ButtonGroup>
            <Popper open={openSearchButtonDropdown} placement="bottom-end" anchorEl={searchButtonRef.current} transition disablePortal>
              {({ TransitionProps }) => (
                <Grow
                  {...TransitionProps}
                  style={{
                    transformOrigin: 'right top',
                  }}
                >
                  <Paper>
                    <ClickAwayListener onClickAway={handleSearchButtonDropdownClose}>
                      <MenuList>
                        <MenuItem
                          disabled={exportLoading}
                          onClick={() => handleExportClick()}
                        >
                          {`${t('export')} (XLSX)`}
                        </MenuItem>
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </Grid>
        </Grid>
      </form>
      <AddPermitDialog open={openAddPermitDialog} setOpen={setOpenAddPermitDialog} />
    </>
  )
}

export default Toolbar
